import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { apiClient } from '@/api/client'
import { useTeam } from '@/hooks/useTeam'
import { useDeleteGameAsAdmin } from '@/hooks/useGames'
import { useIsTeamAdmin } from '@/hooks/useTeamAdmin'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { EditGameModal } from '@/components/game-entry/EditGameModal'
import { Plus, Gamepad2, Trophy, Trash2, Edit2 } from 'lucide-react'
import { useState } from 'react'

interface GameParticipant {
  id: string
  user_id: string
  team: 'red' | 'blue'
  role: 'defense' | 'attack' | 'full'
  user: {
    id: string
    display_name: string
  }
}

interface Game {
  id: string
  date: string
  game_type: '1v1' | '2v2'
  target_score: number
  final_red_score: number
  final_blue_score: number
  winner?: 'red' | 'blue'
  quick_entry: boolean
  participants: GameParticipant[]
}

export function Games() {
  const { getAccessTokenSilently } = useAuth0()
  const { currentTeam } = useTeam()
  const deleteGame = useDeleteGameAsAdmin()
  const { data: isTeamAdmin } = useIsTeamAdmin(currentTeam?.id)
  const [editingGame, setEditingGame] = useState<Game | null>(null)

  const { data: games, isLoading } = useQuery({
    queryKey: ['games', currentTeam?.id],
    queryFn: async () => {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          scope: 'openid profile email',
        },
      })
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
      const response = await apiClient.get(`/games?team_id=${currentTeam?.id}`)
      return response.data
    },
    enabled: !!currentTeam?.id,
  })

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getGameTypeLabel = (gameType: string) => {
    return gameType === '1v1' ? '1v1' : '2v2'
  }

  const getWinner = (game: Game) => {
    if (game.winner) {
      return game.winner
    }
    return 'tie'
  }

  const getParticipantsByTeam = (participants: GameParticipant[], team: 'red' | 'blue') => {
    return participants
      .filter(p => p.team === team)
      .map(p => p.user.display_name)
      .join(', ')
  }

  const handleDeleteGame = async (gameId: string, e: React.MouseEvent) => {
    e.preventDefault() // Prevent navigation to game detail
    e.stopPropagation()
    
    if (window.confirm('Are you sure you want to delete this game? This action cannot be undone.')) {
      try {
        await deleteGame.mutateAsync(gameId)
      } catch (error) {
        console.error('Failed to delete game:', error)
        alert('Failed to delete game. You may not have permission.')
      }
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Games</h1>
          <p className="mt-1 text-sm text-gray-500">
            View and manage your games
          </p>
        </div>
        <Link
          to="/games/new"
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Game
        </Link>
      </div>

      {!games || games.length === 0 ? (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="text-center">
              <Gamepad2 className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No games yet</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by recording your first game.
              </p>
              <div className="mt-6">
                <Link
                  to="/games/new"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Record Game
                </Link>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="space-y-3">
              {games.map((game: Game) => {
                const winner = getWinner(game)
                const redParticipants = getParticipantsByTeam(game.participants, 'red')
                const blueParticipants = getParticipantsByTeam(game.participants, 'blue')

                return (
                  <div
                    key={game.id}
                    className="relative border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all overflow-hidden"
                  >
                    <Link to={`/games/${game.id}`} className="block">
                      {/* Header with game info */}
                      <div className="px-4 pt-3 pb-2 border-b border-gray-100 flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Gamepad2 className="h-4 w-4 text-gray-400" />
                          <span className="text-sm font-medium text-gray-600">
                            {getGameTypeLabel(game.game_type)}
                          </span>
                          <span className="text-xs text-gray-400">•</span>
                          <span className="text-sm text-gray-500">
                            {formatDate(game.date)}
                          </span>
                          {game.quick_entry && (
                            <>
                              <span className="text-xs text-gray-400">•</span>
                              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                Quick Entry
                              </span>
                            </>
                          )}
                        </div>
                        {/* Admin buttons - positioned in header to avoid overlap */}
                        {isTeamAdmin && (
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                setEditingGame(game)
                              }}
                              className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                              title="Edit game"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={(e) => handleDeleteGame(game.id, e)}
                              className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                              title="Delete game"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Main score display - Red | Score | Blue */}
                      <div className="px-4 py-4">
                        <div className="grid grid-cols-3 gap-4 items-center">
                          {/* Red Team - Left */}
                          <div className={`text-left p-3 rounded-lg ${winner === 'red' ? 'bg-red-50 border-2 border-red-300' : 'bg-gray-50'}`}>
                            <div className="text-xs font-medium text-red-600 mb-1">RED TEAM</div>
                            <div className="text-sm text-gray-700 font-medium">{redParticipants || 'No players'}</div>
                          </div>

                          {/* Score - Center */}
                          <div className="text-center">
                            <div className="flex items-center justify-center space-x-3">
                              {/* Red Score with Trophy if winner */}
                              <div className="flex items-center space-x-2">
                                {winner === 'red' && (
                                  <Trophy className="h-5 w-5 text-yellow-500" />
                                )}
                                <span className={`text-4xl font-bold ${winner === 'red' ? 'text-red-600' : 'text-gray-700'}`}>
                                  {game.final_red_score}
                                </span>
                              </div>
                              
                              {/* Dash separator */}
                              <span className="text-2xl font-medium text-gray-400">-</span>
                              
                              {/* Blue Score with Trophy if winner */}
                              <div className="flex items-center space-x-2">
                                <span className={`text-4xl font-bold ${winner === 'blue' ? 'text-blue-600' : 'text-gray-700'}`}>
                                  {game.final_blue_score}
                                </span>
                                {winner === 'blue' && (
                                  <Trophy className="h-5 w-5 text-yellow-500" />
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Blue Team - Right */}
                          <div className={`text-right p-3 rounded-lg ${winner === 'blue' ? 'bg-blue-50 border-2 border-blue-300' : 'bg-gray-50'}`}>
                            <div className="text-xs font-medium text-blue-600 mb-1">BLUE TEAM</div>
                            <div className="text-sm text-gray-700 font-medium">{blueParticipants || 'No players'}</div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* Edit Game Modal */}
      {editingGame && (
        <EditGameModal
          game={editingGame}
          isOpen={!!editingGame}
          onClose={() => setEditingGame(null)}
        />
      )}
    </div>
  )
}
